import { z } from 'zod'

// The payment options. These exact values are what the order form submits and what is saved in
// the `pagesa` column of the orders collection; their visible labels come from the CMS.
export const PAYMENT_METHODS = ["Pagesë përmes postierit", "Pagesë përmes bankës"] as const

// Loose Kosovo / international phone shape: 8 to 15 digits with optional +, spaces and dashes.
const PHONE_RE = /^[+\d][\d\s().-]{7,18}$/

type MessageKey = 'required' | 'email' | 'phone' | 'min' | 'max' | 'intMin' | 'intMax' | 'select'

interface OrderSchemaOptions {
    // Codes of the packages in the CMS ("Standard", "Premium").
    packageCodes: string[]
    // Reads a site_texts value; validation messages live under order.validation.*
    text: (key: string) => string
}

// Shared by the order form (browser) and /api/order (server), so both validate the same way.
export const createOrderSchema = ({ packageCodes, text }: OrderSchemaOptions) => {
    const fmt = (key: MessageKey, n?: number): string => {
        const message = text(`order.validation.${key}`)

        return n === undefined ? message : message.replace('{n}', String(n))
    }

    const requiredString = (msg = fmt('required')) => z.string({ required_error: msg, invalid_type_error: msg }).trim()

    const pako = packageCodes.length > 0
        ? z.enum(packageCodes as [string, ...string[]], { errorMap: () => ({ message: fmt('required') }) })
        : requiredString().min(1, fmt('required'))

    return z.object({
        pako,
        emri: requiredString().min(2, fmt('min', 2)).max(120, fmt('max', 120)),
        email: requiredString().min(1, fmt('required')).email(fmt('email')),
        tel: requiredString().min(1, fmt('required')).regex(PHONE_RE, fmt('phone')),
        tel2: requiredString().min(1, fmt('required')).regex(PHONE_RE, fmt('phone')),
        sasia: z.coerce
            .number({ invalid_type_error: fmt('required'), required_error: fmt('required') })
            .int(fmt('required'))
            .min(1, fmt('intMin', 1))
            .max(999, fmt('intMax', 999)),
        porosiShtese: z.boolean().optional().default(false),
        pagesa: z.enum(PAYMENT_METHODS, { errorMap: () => ({ message: fmt('select') }) }),
        qyteti: requiredString(fmt('select')).min(1, fmt('select')),
        adresa: requiredString().min(5, fmt('min', 5)).max(300, fmt('max', 300)),
        mesazhi: z.string().trim().max(2000, fmt('max', 2000)).optional().default(''),
        // Honeypot, must stay empty.
        hp_subject: z.string().optional().default('')
    })
}

export type OrderSchema = ReturnType<typeof createOrderSchema>
export type OrderInput = z.infer<OrderSchema>
